import type { ParticipantEditFormValues } from '../../../domain/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import cn from 'classnames'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useBookingDetailsActions } from '@/scopes/account/booking-details/infrastructure/dependencies/dependencies'
import { getDefaultPhoneCountry } from '@/scopes/common/domain/policies/defaultPhoneCountry'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useBookingDetailsStore } from '../../../infrastructure/useBookingDetailsStore'
import { getParticipantEditFields, getParticipantEditFormValues } from '../../selectors'
import { ParticipantEditExternalRequestBanner } from './ParticipantEditExternalRequestBanner'
import { ParticipantEditExternalRequestForm } from './ParticipantEditExternalRequestForm'
import { ParticipantEditNoChangesBanner } from './ParticipantEditNoChangesBanner'
import { ParticipantEditSaveErrorBanner } from './ParticipantEditSaveErrorBanner'
import { ParticipantEditSavingMessage } from './ParticipantEditSavingMessage'
import { ParticipantEditSections } from './ParticipantEditSections'
import styles from './ParticipantEditShared.module.css'

export const PARTICIPANT_EDIT_FORM_ID = 'participant-edit-form'

export function ParticipantEditForm() {
  const { formatMessage } = useIntl()
  const { participantEditSaveRequested, participantEditUnchangedSubmitted } = useBookingDetailsActions()
  const loadStatus = useBookingDetailsStore(state => state.participantEdit.loadStatus)
  const submitStatus = useBookingDetailsStore(state => state.participantEdit.submitStatus)
  const isExternalRequestOpen = useBookingDetailsStore(state => state.participantEdit.externalRequest.isOpen)
  const locale = useConfiguration('locale')
  const fields = useBookingDetailsStore(state => getParticipantEditFields(state, 'standard'))
  const values = useBookingDetailsStore(state => getParticipantEditFormValues(state, 'standard', getDefaultPhoneCountry(locale)))
  const methods = useForm<ParticipantEditFormValues>({ values })
  const { isDirty } = methods.formState
  const wasSubmittedUnchanged = useBookingDetailsStore(state => state.participantEdit.wasSubmittedUnchanged)

  if (loadStatus === 'loading' || loadStatus === 'idle') {
    return (
      <div className={styles.loaderContainer}>
        <IndeterminateProgressCircle />
      </div>
    )
  }

  if (loadStatus === 'error') {
    return (
      <Banner hasIcon color="danger" size="M">
        <Banner.TextsWrapper>
          <Banner.Text>{formatMessage({ id: 'common_error_title' })}</Banner.Text>
          <Banner.SupportingText>{formatMessage({ id: 'common_error_message' })}</Banner.SupportingText>
        </Banner.TextsWrapper>
      </Banner>
    )
  }

  const isSubmitting = submitStatus === 'loading'

  function handleSubmit(values: ParticipantEditFormValues) {
    return isDirty ? participantEditSaveRequested(values) : participantEditUnchangedSubmitted()
  }

  return (
    <>
      {isSubmitting && <ParticipantEditSavingMessage />}
      {isExternalRequestOpen && <ParticipantEditExternalRequestForm />}
      <FormProvider {...methods}>
        <form
          id={PARTICIPANT_EDIT_FORM_ID}
          className={cn(styles.form, (isSubmitting || isExternalRequestOpen) && styles.formHidden)}
          onSubmit={methods.handleSubmit(handleSubmit)}
          noValidate
        >
          {submitStatus === 'error' && <ParticipantEditSaveErrorBanner />}
          {wasSubmittedUnchanged && !isDirty && <ParticipantEditNoChangesBanner />}
          <ParticipantEditExternalRequestBanner />
          <ParticipantEditSections fields={fields} />
        </form>
      </FormProvider>
    </>
  )
}
